import React, { useState } from 'react';
import { Button, PageHeader, Popconfirm, Space, Spin, Tag, Typography } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';
import firebase from 'firebase/app';
import 'antd/dist/antd.css';
import '../../../App.scss';

import { useClient } from '../../../models/useClient';
import { useMcpLive } from '../../../utils/aiConnections';
import { useIsStrikeAdmin } from '../../../models/useIsStrikeAdmin';
import AddLoginMethodSection from '../../../components/client/AddLoginMethodSection';
import CloseAccountSection from '../../../components/client/CloseAccountSection';

const { Text } = Typography;

// /client/manage/:clientId/security  (also /admin/clients/:clientId/security)
export default function ClientSecurityScreen(props) {
  const clientId = props.match && props.match.params ? props.match.params.clientId : null;
  const isAdmin = !!props.isAdmin;
  const [loading, client] = useClient(clientId);
  const mcpLive = useMcpLive();
  const ownerId = client?.ownerId || null;
  const [, isStrikeAdmin] = useIsStrikeAdmin(isAdmin ? ownerId : null);
  const aiConnectionsPath = isAdmin
    ? `/admin/clients/${clientId}/ai-connections`
    : `/client/manage/${clientId}/ai-connections`;
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [adminError, setAdminError] = useState(null);

  const toggleAdmin = async (makeAdmin) => {
    if (!ownerId) return;
    setSavingAdmin(true);
    setAdminError(null);
    try {
      const ref = firebase
        .firestore()
        .collection('clients')
        .doc('strike')
        .collection('admins')
        .doc(ownerId);
      if (makeAdmin) {
        await ref.set({ role: 'Admin' });
      } else {
        await ref.delete();
      }
    } catch (err) {
      console.error('[ClientSecurityScreen] Failed to toggle admin:', err);
      setAdminError(err.message || 'Failed to update admin status');
    } finally {
      setSavingAdmin(false);
    }
  };

  return (
    <div style={{ padding: '16px', maxWidth: 520 }}>
      <PageHeader
        ghost={false}
        title="Security"
        subTitle={isAdmin ? `Manage login methods for this client` : 'Manage how you sign in'}
        onBack={() => window.history.back()}
        style={{ paddingLeft: 0, paddingRight: 0 }}
      />

      {loading && <Spin />}

      {isAdmin && !loading && ownerId && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8 }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Space>
              <SafetyOutlined />
              <Text strong>Admin Access</Text>
              {isStrikeAdmin ? (
                <Tag color="red" icon={<SafetyOutlined />}>Strike Admin</Tag>
              ) : (
                <Tag color="default">Not an admin</Tag>
              )}
            </Space>
            <Space>
              {isStrikeAdmin ? (
                <Popconfirm
                  title="Remove admin access?"
                  description="This will remove this user from Strike admins."
                  okText="Remove"
                  okType="danger"
                  cancelText="Cancel"
                  onConfirm={() => toggleAdmin(false)}
                >
                  <Button size="small" danger loading={savingAdmin}>Remove Admin</Button>
                </Popconfirm>
              ) : (
                <Popconfirm
                  title="Grant admin access?"
                  description="This will give this user full Strike admin access."
                  okText="Make Admin"
                  cancelText="Cancel"
                  onConfirm={() => toggleAdmin(true)}
                >
                  <Button size="small" icon={<SafetyOutlined />} loading={savingAdmin}>Make Admin</Button>
                </Popconfirm>
              )}
              {adminError && <Text type="danger" style={{ fontSize: 12 }}>{adminError}</Text>}
            </Space>
          </Space>
        </div>
      )}

      {!loading && client && (
        <AddLoginMethodSection
          client={client}
          onUpdated={() => {}}
          isAdmin={isAdmin}
          adminTargetUid={isAdmin ? client.ownerId : null}
        />
      )}

      {mcpLive === true && !loading && client && (
        <div style={{ margin: '16px 0', padding: '12px 16px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8 }}>
          <Text strong>AI connections</Text>
          <div>
            <Text type="secondary">
              {isAdmin ? 'AI assistants connected to this account. ' : 'AI assistants you have connected, and where to pause or disconnect them. '}
            </Text>
            <a
              href={aiConnectionsPath}
              onClick={(e) => {
                if (props.history && props.history.push) {
                  e.preventDefault();
                  props.history.push(aiConnectionsPath);
                }
              }}
            >
              Manage AI connections
            </a>
          </div>
        </div>
      )}

      {!loading && client && (
        <CloseAccountSection client={client} clientId={clientId} isAdmin={isAdmin} />
      )}
    </div>
  );
}
